export const normalizeRoleName = (role) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ");

export const normalizeTabName = (tabName) =>
  String(tabName || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ");

const singularizeTabName = (tabName) =>
  normalizeTabName(tabName)
    .split(" ")
    .map((word) => (word.length > 3 && word.endsWith("s") ? word.slice(0, -1) : word))
    .join(" ");

const canonicalizeTabName = (tabName) =>
  singularizeTabName(tabName)
    .replace(/\bspecialisation\b/g, "specialization")
    .replace(/^manage /, "")
    .replace(/\benquir(y|ie)\b/g, "inquiry")
    .replace(/\binquir(y|ie)\b/g, "inquiry")
    .replace(/\blearn seo\b/g, "learn")
    .replace(/\blogin attempt\b/g, "login analytic");

export const doTabNamesMatch = (firstTabName, secondTabName) => {
  const first = normalizeTabName(firstTabName);
  const second = normalizeTabName(secondTabName);
  return (
    first === second ||
    singularizeTabName(first) === singularizeTabName(second) ||
    canonicalizeTabName(first) === canonicalizeTabName(second)
  );
};

export const isAdminRole = (role) => {
  const roleName = String(role || "").trim();
  return roleName === "admin" || roleName === "Super Admin";
};

export const isAdminUser = () => isAdminRole(localStorage.getItem("userRole"));

export const normalizeTabIds = (value) => {
  if (value == null || value === "") return [];

  if (Array.isArray(value)) {
    return value
      .map((tabId) => Number(tabId))
      .filter((tabId) => Number.isFinite(tabId));
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? [value] : [];
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) return [];

    try {
      return normalizeTabIds(JSON.parse(trimmedValue));
    } catch {
      return trimmedValue
        .split(",")
        .map((tabId) => Number(tabId.trim()))
        .filter((tabId) => Number.isFinite(tabId));
    }
  }

  return [];
};

export const getStoredTabIds = () => {
  try {
    return normalizeTabIds(localStorage.getItem("tab_id"));
  } catch {
    return [];
  }
};

export const getBackendTabId = (tab) =>
  Number(tab?.tab_id ?? tab?.tabId ?? tab?.id);

export const getBackendTabName = (tab) =>
  tab?.tab_name ?? tab?.tabName ?? tab?.name ?? tab?.title ?? "";

export const normalizeBackendTabs = (tabs) =>
  Array.isArray(tabs)
    ? tabs
        .map((tab) => ({
          ...tab,
          tab_id: getBackendTabId(tab),
          tab_name: getBackendTabName(tab),
        }))
        .filter((tab) => Number.isFinite(tab.tab_id) && tab.tab_name)
    : [];

export const getStoredBackendTabs = () => {
  try {
    return normalizeBackendTabs(JSON.parse(localStorage.getItem("backend_tabs") || "[]"));
  } catch {
    return [];
  }
};

export const findBackendTabByName = (tabs, tabName) => {
  const tabNames = Array.isArray(tabName) ? tabName : [tabName];
  return normalizeBackendTabs(tabs).find(
    (tab) => tabNames.some((name) => doTabNamesMatch(tab.tab_name, name))
  );
};

export const hasStoredTabAccess = (tabName) => {
  if (isAdminUser()) return true;

  const backendTab = findBackendTabByName(getStoredBackendTabs(), tabName);
  if (!backendTab) return false;

  return getStoredTabIds().includes(backendTab.tab_id);
};

